/**
 * Phase 36 — a running experiment: the day it is on as a band, the two
 * sides so far (SMS, the metric), the result's sentence — and Stop early /
 * Refresh for whoever may manage it.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, useState } from 'react';
import type { DropGenExperimentRow } from '@trafficpulse/shared';
import { EXPERIMENT_METRIC_WORDS, experimentValue } from '@trafficpulse/shared';

import { Button, ConfirmDialog } from '@/components/ui';

import { EXPERIMENT_HONESTY, dayWords, ddmm } from './experiment-copy';

interface Props {
  experiment: DropGenExperimentRow;
  label: string;
  /** False on a follower reading its master's experiment. */
  canManage: boolean;
  busy: boolean;
  onStop?: () => void;
  onRefresh?: () => void;
}

export const ExperimentProgress: FC<Props> = ({ experiment: e, label, canManage, busy, onStop, onRefresh }) => {
  const [confirm, setConfirm] = useState(false);
  const pct = Math.min(100, Math.max(0, (Math.min(e.dayOf, e.days) / e.days) * 100));
  const r = e.result;
  return (
    <div className="flex flex-col gap-3" data-testid="experiment-progress">
      <div className="flex flex-wrap items-baseline justify-between gap-2 text-sm">
        <span>
          <b>{label}</b> gets <b>{e.sharePct} %</b> of the drops · compared on the {EXPERIMENT_METRIC_WORDS[e.metric]}
        </span>
        <span className="font-mono text-xs tabular-nums text-muted-foreground">
          {dayWords(e)} · {ddmm(e.startDate)} – {ddmm(e.endDate)}
        </span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted" role="progressbar" aria-valuenow={Math.round(pct)} aria-valuemin={0} aria-valuemax={100}>
        <div className="h-full rounded-full bg-primary transition-[width] duration-500" style={{ width: `${pct}%` }} />
      </div>
      {r ? (
        <div className="grid gap-2 text-xs sm:grid-cols-2">
          {[r.experiment, r.others].map((side) => (
            <div key={side.label} className="rounded-md border border-border/60 px-3 py-2">
              <div className="flex items-baseline justify-between">
                <b>{side.label}</b>
                <span className="font-mono tabular-nums">{experimentValue(r.metric, side.value)}</span>
              </div>
              <div className="text-muted-foreground">
                {side.smsSent.toLocaleString('en-US')} SMS · {side.drops} drop{side.drops === 1 ? '' : 's'} · {side.conversions} conv.
              </div>
            </div>
          ))}
          <p className="sm:col-span-2 text-muted-foreground">{r.words}</p>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">No judged send yet — the first comparison lands with the next verdict pass (every 15 minutes while the day runs).</p>
      )}
      <p className="text-[11px] text-muted-foreground">{EXPERIMENT_HONESTY}</p>
      {canManage ? (
        <div className="flex flex-wrap gap-2">
          {onRefresh ? (
            <Button size="sm" variant="outline" disabled={busy} onClick={onRefresh}>
              Refresh the result
            </Button>
          ) : null}
          {onStop ? (
            <Button size="sm" variant="outline" disabled={busy} onClick={() => setConfirm(true)}>
              Stop early
            </Button>
          ) : null}
        </div>
      ) : null}
      <ConfirmDialog
        open={confirm}
        title="Stop the experiment early?"
        description={`${label} stops getting its ${e.sharePct} % from the next re-sync; the result so far is kept and you can still apply it.`}
        confirmLabel="Stop early"
        busy={busy}
        onConfirm={() => {
          setConfirm(false);
          onStop?.();
        }}
        onCancel={() => setConfirm(false)}
      />
    </div>
  );
};
